import { toast } from "sonner";

import type {
  MissionCompletedNotificationSchema,
  MissionPrizePoolExhaustedNotificationSchema,
  MissionSecondaryTaskCompleteNotificationSchema,
} from "@/types";
import { MissionToast } from "@/components/notification/mission-toast";
import { useAppStore } from "@/stores";
import { postPrizePayout } from "./host-events";
import { ownsInGameNotifications } from "./notifications";

/** The mission types the widget displays (everything else on the stream is
 *  acked-and-dropped by the handler). `mission_prize_pool_exhausted` is the
 *  Sprint Mission "no more winners" message — sent to the player who finished
 *  after the pool emptied, and broadcast to everyone still mid-mission (#3958). */
export type MissionNotificationSchema =
  | MissionCompletedNotificationSchema
  | MissionSecondaryTaskCompleteNotificationSchema
  | MissionPrizePoolExhaustedNotificationSchema;

const MISSION_NOTIFICATION_TYPES = new Set([
  "mission_completed",
  "mission_secondary_task_complete",
  "mission_prize_pool_exhausted",
]);

export function isMissionNotification(
  notification: { type?: string } & object
): notification is MissionNotificationSchema {
  return MISSION_NOTIFICATION_TYPES.has(notification.type ?? "");
}

/** Narrow the pair by hand: the generated schemas declare `type` as *optional*
 *  and carry a `[k: string]: unknown` index signature, so an inline
 *  `notification.type === …` check leaves the union intact and every field reads
 *  back as `unknown` (same trap as the `Extract<>` one in notifications.handler). */
export function isSecondaryTaskNotification(
  notification: MissionNotificationSchema
): notification is MissionSecondaryTaskCompleteNotificationSchema {
  return notification.type === "mission_secondary_task_complete";
}

export function isPoolExhaustedNotification(
  notification: MissionNotificationSchema
): notification is MissionPrizePoolExhaustedNotificationSchema {
  return notification.type === "mission_prize_pool_exhausted";
}

/**
 * Whether this notification has anything to SAY. A plain mid-ladder mission
 * completion without a prize and without repetitions carries no copy (product
 * call: nothing worth interrupting a spin for) — it is acked like everything
 * else but never occupies a toast slot. Secondary tasks and the pool-exhausted
 * message always have copy.
 */
export function isToastableMissionNotification(
  notification: MissionNotificationSchema
): boolean {
  if (isSecondaryTaskNotification(notification)) return true;
  if (isPoolExhaustedNotification(notification)) return true;
  return (
    notification.prize_title !== "" ||
    notification.remaining_tasks_amount === 0 ||
    notification.number_of_repetitions > 1
  );
}

/**
 * Toast delivered mission notifications. Deduped against `seenNotificationIds`
 * — mission rows are acked on receipt, but a lost ack *response* leaves them
 * pending server-side and they re-arrive on the next resubscribe, which would
 * otherwise toast the same completion twice.
 *
 * A pending row is toasted whatever its age: only this surface acks it (see
 * the notifications handler), so a row still pending is one the player has not
 * seen — a deposit task finished in the lobby is announced when they open a
 * game, as the legacy overlay did.
 */
export function showMissionNotifications(
  notifications: MissionNotificationSchema[]
): void {
  const state = useAppStore.getState();

  // Only the surface that owns the casino's in-game notifications toasts them (the overlay, or
  // the widgets on a provider brand — `Casino.is_game_provider`, delivered on `configure`, which
  // always lands before the notifications stream subscribes, see store-subscriptions' config
  // gate). Rows are still acked by the handler, so nothing piles up server-side; deliberately NOT
  // marked seen, so flipping the casino to provider mid-session lets a re-delivery toast normally.
  if (!ownsInGameNotifications(state)) return;

  const unseen = notifications.filter(
    (n) => !(n.id in state.seenNotificationIds)
  );
  if (unseen.length === 0) return;

  // Mark everything seen, including what has no copy below: its ack is
  // already on its way (see the handler).
  state.markNotificationsSeen(unseen.map((n) => n.id));

  const toasted: number[] = [];
  for (const notification of unseen) {
    if (!isToastableMissionNotification(notification)) continue;
    toasted.push(notification.campaign_id);
    toast.custom((id) => (
      <MissionToast
        notification={notification}
        onDismiss={() => toast.dismiss(id)}
      />
    ));
    if (!isPoolExhaustedNotification(notification)) {
      postPrizePayout(notification.id, { text: notification.prize_title });
    }
  }
  state.noteCampaignNews(toasted);
}
